import { redirect, setDataFromPath } from "@transact-open-ux/core";
import React, {
  ComponentType,
  ConsumerProps,
  FunctionComponent,
  useContext,
  useEffect,
  useReducer
} from "react";
import { CurrentPageMeta, navigateAction } from "../types";
import { useTransactContext } from "./TransactProvider";

export interface PageProviderState {
  /**
   * The name of the current page displayed
   */
  currentPage?: string;
  /**
   * An Object that contains the currently displayed page's meta data.
   *
   * This can be used to populate current page information around your app.
   *
   * `{
   *   id: "AboutYou",
   *   label: "About You",
   *   stepNumber: 1,
   *   offMenu: false
   * }`
   *
   */
  currentPageMeta?: CurrentPageMeta | object;
  /**
   * A boolean property that can be used to show/hide or disable/enable a back button.
   *
   * When the Transact Application Framework is enabled, the value will be based on the data returned from Transact Manager.
   *
   * Otherwise it will be `true` IF the current page is the first page in navPages.
   */
  disableBack?: boolean;
  /**
   * A boolean property that can be used to show/hide or disable/enable a submit button.
   *
   * When the Transact Application Framework is enabled, the value will be based on the data returned from Transact Manager.
   *
   * Otherwise it will be `true` IF the current page is NOT the last page in navPages.
   */
  disableSubmit?: boolean;
  /**
   * Returns the formApi set via the TransactForm component.
   * If you are not using the TransactForm component and have not set this up manually, this will be undefined.
   */
  getFormApi?: any;
  /**
   * Navigates to the page found with the page id or number provided.
   * @param pageIdOrNum
   * @param performFormUpdate
   * @param skipValidation
   */
  goToPage?: (
    pageIdOrNum: string | number,
    skipValidation?: boolean,
    performFormUpdate?: boolean
  ) => void;
  /**
   * Gets the currently displayed page, this is used by the <PageController /> component
   *
   * It is recommended you use the <PageController /> component to render the pages
   */
  getCurrentPage?: () => React.ReactElement<any> | null;
  /**
   * A map of all pages, keys are either the id set to the page or the page index
   */
  pageMap?: {
    index?: number;
    page?: React.ReactElement<any> | null;
  };
  /**
   * An array of all pages, including offMenu pages.
   */
  pages?: Array<React.ReactElement<any>>;
  /**
   * The current page index
   */
  index?: number;
  /**
   * A function that is used in the `<PageController/>` component to initialize the pages and the page validation function.
   *
   * It is recommended you use the `<PageController/>` component to initialize your pages.
   */
  initPages?: (
    pages: React.ReactChild[],
    validateFn: () => Promise<boolean>
  ) => void;
  /**
   * An array of pages that are part of the navigation.
   * These are `<Page/>` components that do not have an offMenu prop.
   */
  navPages?: Array<React.ReactElement<any>>;
  /**
   * A function that allows you to navigate between pages via the narrative or by sequential navigation
   *
   * Before navigating, the method will ensure that the form is valid before moving forward.
   * If the form is not valid, a resolved promise will be returned with false as the value.
   *
   * Use navigate("forward") to go forward and navigate("back") to go back.
   * If no value is provided the action will default to "forward"
   */
  navigate?: (action: navigateAction) => Promise<any>;
  /**
   * A boolean property which indicates whether Transact Application Framework support is enabled
   */
  tafEnabled?: boolean;
  validatePage?: () => boolean;
}

export interface PageProviderProps {
  /**
   * The content of the Page Provider.
   */
  children: React.ReactNode;
  /**
   *  A boolean property that when true will skip validation on navigate.
   *  Use this if you only want to validate on submit.
   */
  skipValidate?: boolean;
}

const PageContext = React.createContext({});

const initialState: PageProviderState = {
  currentPage: null,
  disableBack: false,
  disableSubmit: false,
  index: 0,
  navPages: [],
  pageMap: {},
  pages: [],
  validatePage: null
};

type PageAction =
  | { type: "SET_INDEX"; payload: number }
  | { type: "SET_CURRENT_PAGE"; payload: string }
  | { type: "SET_STATE"; payload: object };

const reducer = (state: PageProviderState, action: PageAction) => {
  switch (action.type) {
    case "SET_INDEX":
      return { ...state, index: action.payload };
    case "SET_CURRENT_PAGE":
      return { ...state, currentPage: action.payload };
    case "SET_STATE":
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

/**
 * A component that surrounds your entire app and handles all the page logic.
 * <br>
 * This page logic includes Transact Application Framework support, page change logic, validation and meta data.
 * <br>
 * This component needs to be wrapped within the `TransactProvider` component.
 *
 * ## Example
 * Wrapping your root App component
 * ```jsx
 * import { TransactProvider, PageProvider } from '@transact-open-ux/react';
 * import App from './App';
 *
 * ReactDOM.render(
 *  <TransactProvider>
 *    <PageProvider>
 *     <App />
 *    </PageProvider>
 *  </TransactProvider>,
 * document.getElementById('root')
 * );
 * ```
 */
const PageProvider: FunctionComponent<PageProviderProps> = ({
  children,
  skipValidate
}: PageProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    formUpdate,
    getFormApi,
    getNarrativeData,
    saveChallengeLoading
  } = useTransactContext();
  const { navPages, pageMap, validatePage, tafEnabled } = state;
  const currentNarrativePage = getNarrativeData("CurrentPage");

  const initPages = (pages, validatePageFn) => {
    const initialNavPages = [];
    const initialPageMap = {};

    pages.forEach((page, index) => {
      const {
        props: { id, offMenu }
      } = page;
      const key = id || index;

      // use id (if available) otherwise use index as id
      initialPageMap[key] = {
        index,
        page
      };

      if (!offMenu) {
        initialNavPages.push(page);
      }
    });

    dispatch({
      payload: {
        navPages: initialNavPages,
        pageMap: initialPageMap,
        pages,
        tafEnabled: Object.keys(getNarrativeData() || {}).length >= 2,
        validatePage: validatePageFn
      },
      type: "SET_STATE"
    });
  };

  const getCurrentPage = () => {
    const { index, pages } = state;

    // Don't render if save challenge is loading or if there aren't any pages initialized
    // re-render page to ensure new data is re-initialized correctly
    if (saveChallengeLoading || (!pages || !pages.length)) {
      return null;
    }

    return pages[index];
  };

  const getCurrentPageMeta = (): CurrentPageMeta => {
    const page = getCurrentPage();

    if (page) {
      const {
        props: { id, label, offMenu }
      } = page;

      const index = navPages.indexOf(page);
      const stepNumber = index >= 0 ? index + 1 : null;

      return {
        id,
        label,
        offMenu,
        stepNumber
      };
    }

    return {
      offMenu: true
    };
  };

  async function goToPage(
    pageIdOrNum: string | number,
    skipValidation: boolean,
    performFormUpdate: boolean
  ) {
    const formApi = (getFormApi && getFormApi()) || {};
    const valid = skipValidation || (await validatePage());

    if (valid) {
      if (typeof pageIdOrNum === "number") {
        pageIdOrNum = pageIdOrNum - 1;
      }

      setDataFromPath(
        "SFMData.NarrativeMeta.CurrentPage",
        formApi.values,
        pageIdOrNum
      );

      const { index } = pageMap[pageIdOrNum];

      dispatch({
        payload: index,
        type: "SET_INDEX"
      });

      if (performFormUpdate) {
        formUpdate(formApi.values);
      }
    }
  }

  async function navigate(action: navigateAction = "forward") {
    const formApi = (getFormApi && getFormApi()) || {};

    // Handle validation
    if (
      !skipValidate &&
      action !== "back" &&
      typeof validatePage === "function"
    ) {
      const valid = await validatePage();
      if (!valid) {
        return Promise.resolve(valid);
      }
    }

    if (tafEnabled) {
      // set narrative action type
      setDataFromPath("SFMData.NarrativeMeta.Action", formApi.values, action);
    } else {
      const { pages } = state;
      let { index } = state;

      index =
        action === "forward"
          ? Math.min(index + 1, pages.length)
          : Math.max(index - 1, 0);

      // update current page for save/resume capability
      const {
        props: { id }
      } = pages[index];

      setDataFromPath(
        "SFMData.NarrativeMeta.CurrentPage",
        formApi.values,
        id || index
      );

      dispatch({
        payload: index,
        type: "SET_INDEX"
      });
    }

    // perform formUpdate
    return formUpdate(formApi.values);
  }

  useEffect(() => {
    // This lifecycle method is used to update the index and currentPage
    // of the component based on changes coming from server or elsewhere
    if (
      currentNarrativePage &&
      currentNarrativePage !== state.currentPage &&
      pageMap.hasOwnProperty(currentNarrativePage)
    ) {
      const { index } = pageMap[currentNarrativePage];

      dispatch({
        payload: index,
        type: "SET_INDEX"
      });

      dispatch({
        payload: currentNarrativePage,
        type: "SET_CURRENT_PAGE"
      });
    }
  }, [currentNarrativePage]);

  const currentPageNode = getCurrentPage();
  const narrativeMeta = getNarrativeData() || {};
  const {
    DisableSubmit: disableSubmit = !!(
      currentPageNode &&
      navPages.indexOf(currentPageNode) !== navPages.length - 1
    ),
    DisableBack: disableBack = !!(
      currentPageNode && navPages.indexOf(currentPageNode) === 0
    ),
    RedirectUrl
  } = narrativeMeta;

  if (RedirectUrl) {
    redirect(RedirectUrl);
  }

  const ctx = {
    ...state,
    currentPageMeta: getCurrentPageMeta(),
    disableBack,
    disableSubmit,
    getCurrentPage,
    getFormApi,
    goToPage,
    initPages,
    navigate
  };

  return <PageContext.Provider value={ctx}>{children}</PageContext.Provider>;
};

/**
 * A component that can access the page state via render props from inside a `<PageProvider/>` component.
 *
 * ## Example
 *
 * Creating a navigation bar component
 *
 * ```jsx
 * import { PageConsumer } from '@transact-open-ux/react';
 *
 * export default function NavBar() {
 *  return (
 *   <PageConsumer>
 *     {({ navigate, disableSubmit }) => (
 *       <div>
 *         <button onClick={() => navigate("back")} type="button">Back</button>
 *         {DisableSubmit ? (
 *           <button onClick={() => navigate("forward")} type="button">Next</button>
 *         ) : (
 *           <button type="submit">Submit</button>
 *         )}
 *       </div>
 *     )}
 *   </PageConsumer>;
 *  )
 * }
 * ```
 */
const PageConsumer: ComponentType<ConsumerProps<PageProviderState>> =
  PageContext.Consumer;

const usePageContext = () => useContext<PageProviderState>(PageContext);

export { PageConsumer, PageProvider, usePageContext };
